import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import utils from "../../universal/utils";

import PublicPageLayout from "./PublicPageLayout";
import { getPublicSiteSx } from "./publicSiteStyles";
import {
  PRIVACY_EMAIL,
} from "./siteData";
import {
  DEFAULT_DISCORD_INVITE_URL,
  fetchPublicDiscordInviteUrl,
} from "./discordInviteUrl";

export default function ContactPage() {
  const theme = useTheme();
  const sx = React.useMemo(() => getPublicSiteSx(theme), [theme]);
  const [discordInviteUrl, setDiscordInviteUrl] = React.useState(
    DEFAULT_DISCORD_INVITE_URL
  );

  React.useEffect(() => {
    let cancelled = false;

    fetchPublicDiscordInviteUrl(utils.getEdgeBasePath())
      .then((resolvedUrl) => {
        if (!cancelled) {
          setDiscordInviteUrl(resolvedUrl);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const contactCards = React.useMemo(
    () => [
      {
        title: "General support",
        body:
          "Submit a support ticket for questions about the product, account flow, memberships, or anything else that blocks your use of Ouro.",
        actionLabel: "Open a support ticket",
        to: "/support",
        icon: <MailOutlineIcon />,
      },
      {
        title: "Privacy and data requests",
        body:
          "If your request is about account data, privacy, or related rights, use the privacy contact directly so it lands in the right place.",
        actionLabel: PRIVACY_EMAIL,
        actionHref: `mailto:${PRIVACY_EMAIL}`,
        icon: <PolicyOutlinedIcon />,
      },
      {
        title: "Community conversation",
        body:
          "If you want to discuss feedback, ideas, or platform direction with the community around Ouro, Discord is the fastest public channel.",
        actionLabel: "Join Discord",
        actionHref: discordInviteUrl,
        icon: <ForumOutlinedIcon />,
      },
    ],
    [discordInviteUrl]
  );

  return (
    <PublicPageLayout
      eyebrow="Contact"
      title="Reach the people behind Ouro."
      description="Whether you need support, a privacy contact, or the community Discord, here are the right places to reach the team."
      mapBackground
    >
      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Use the right channel first.</Typography>
        </Box>
        <Box sx={sx.cardGrid}>
          {contactCards.map((item) => (
            <Box key={item.title} sx={sx.card}>
              <Box sx={sx.cardIconWrap}>{item.icon}</Box>
              <Typography sx={sx.cardTitle}>{item.title}</Typography>
              <Typography sx={{ ...sx.cardBody, mb: 2 }}>{item.body}</Typography>
              {item.to ? (
                <Typography
                  component={Link}
                  to={item.to}
                  sx={{
                    color: "#E8A33D",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    textDecoration: "none",
                  }}
                >
                  {item.actionLabel}
                </Typography>
              ) : (
                <Typography
                  component="a"
                  href={item.actionHref}
                  target={item.actionHref.startsWith("http") ? "_blank" : undefined}
                  rel={item.actionHref.startsWith("http") ? "noopener noreferrer" : undefined}
                  sx={{
                    color: "#E8A33D",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    textDecoration: "none",
                  }}
                >
                  {item.actionLabel}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </PublicPageLayout>
  );
}
